import color from "color";
import { platformModifierKey, platformModifierKeyOnly } from "ol/events/condition";
import { Point } from "ol/geom";
import { fromLonLat, toLonLat } from "ol/proj";
import { useCallback, useEffect, useMemo, useState, } from "react";
import { renderToString } from 'react-dom/server';
import { useTranslation } from "react-i18next";
import { RFeature, RLayerVector, ROverlay } from "rlayers";
import Icon from "../../common/components/Icon";
import useNetwork from "../../common/hooks/networkHook";
import { distinct } from "../../common/utils/arrayUtilities";
import useCredentials from "../../common/hooks/credentialsHook";
import { canUpdateCrew, updateCrew, useCrews } from "../../reducers/crewSlice";
import { useCrewStatuses } from "../../reducers/crewStatusSlice";
import { canUpdateJob, updateJob, useActiveJobs } from "../../reducers/jobSlice";
import { canUpdateLocation, updateLocation, useLocations } from "../../reducers/locationSlice";
import { useTypes } from "../../reducers/typeSlice";
import { useSelectedCallsign, useSelectedJobid, useSelectedLocation } from "../../utils/CADStateProvider";
import { LeafletMarker } from "../leaflet/LeafletMarker";
import { useLayerManager } from "./LayerManager";
import './Marker.css';
import PropTypes from 'prop-types';
import useClientConfig from "../../common/hooks/clientConfigHook";
import { formatWithFields } from "../../common/utils/objectUtilities";
import { useJobAssistance } from "../../reducers/jobAssistanceSlice";

function toValidCSSClassName(str) {
    // Replace invalid characters at the start
    str = str.replace(/^[^a-zA-Z-_]+/, '');
    // Replace any character that is not a letter, digit, underscore, or hyphen with an empty string
    str = str.replace(/[^a-zA-Z0-9-_]/g, '');
    return str;
  }

const locationIconHTML = (location, showLabel, formatString, opacity, onClick) => {
    const iconColor = location.color ?? 'blue';
    const isDark = color(iconColor).isDark();
    const icon = <><style>{`
        .locationMarker${location.internalid.replace('-', '')} {
            width: 32px;
            height: 32px;
            line-height: 32px;
            border-radius: 5px;
            border-width: 0px;
            position: relative;
            background-color: ${iconColor};
            color: ${isDark ? 'white' : 'black'};
            padding-top: auto;
            padding-bottom: auto;
            text-align: center;
            transform: rotate(45deg);
            opacity: ${opacity};
            user-select: none;
        }`}</style>
        { showLabel ? <div style={{ lineHeight: '12px', marginBottom: '4px', paddingTop: '2px', paddingBottom: '2px', minWidth: '32px', textAlign: 'center', whiteSpace: 'nowrap', backgroundColor: 'rgba(255,255,255,0.8)', transform: 'translateX(calc(-50% + 16px))', opacity }}>{formatString ? formatWithFields(formatString, location) : location.name}</div> : null }
        <div className={"locationMarker" + location.internalid.replace('-', '')}>
            <Icon literal={location?.icon} style={{transform: 'rotate(-45deg)'}}></Icon>
        </div>
    </>
    if (onClick) {
        return <div style={{ cursor: 'pointer' }} onClick={onClick}>
            {icon}
        </div>;
    } else {
        return icon;
    }
}

const jobIconHTML = (t, job, type, showLabel, opacity, onClick) => {
    const isDark = type?.color ? color(type.color).isDark() : true;
    const iconColor = type?.color ?? 'blue';
    return <div>
    <style>{`
    .jobMarker${job.jobid} {
        width: 32px;
        height: 32px;
        line-height: 32px;
        border-radius: 16px;
        border-width: 0px;
        position: relative;
        background-color: ${iconColor};
        color: ${isDark ? 'white' : 'black'};
        padding-top: auto;
        padding-bottom: auto;
        text-align: center;
        opacity: ${opacity};
        user-select: none;
    }


    .jobMarker${job.jobid}::after {
        content: '\u200b';
        position: absolute;
        bottom: -38px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 16px solid transparent;
        border-right: 16px solid transparent;
        border-top: 16px solid ${iconColor};
        opacity: ${opacity};
        user-select: none;
    }`}</style>
    { showLabel ? <div style={{ lineHeight: '12px', marginBottom: '4px', paddingTop: '2px', paddingBottom: '2px', minWidth: '32px', textAlign: 'center', whiteSpace: 'nowrap', backgroundColor: 'rgba(255,255,255,0.8)', transform: 'translateX(calc(-50% + 16px))', opacity }}>{t('job', { count: 1 }) + ' ' + job.displayid}</div> : null }
    <div className={"jobMarker" + job.jobid} style={{ cursor: 'pointer' }} onClick={onClick}>
        <Icon literal={type?.icon}></Icon>
    </div>
    </div>;
}

const crewIconHTML = (crew, type, showLabel, opacity, onClick) => {
    const c = crew.color ?? type?.color;
    const isDark = c ? color(c).isDark() : true;
    const iconColor = c ?? 'blue';
    const name = toValidCSSClassName(crew.callsign);
    return <div>
    <style>{`
    .crewMarker${name} {
        width: 32px;
        height: 32px;
        line-height: 32px;
        border-radius: 5px;
        border-width: 0px;
        position: relative;
        background-color: ${iconColor};
        color: ${isDark ? 'white' : 'black'};
        padding-top: auto;
        padding-bottom: auto;
        text-align: center;
        opacity: ${opacity};
        user-select: none
    }


    .crewMarker${name}::after {
        content: '\u200b';
        position: absolute;
        bottom: -38px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid ${iconColor};
        opacity: ${opacity};
        user-select: none
    }`}</style>
    { showLabel ? <div style={{ lineHeight: '12px', marginBottom: '4px', paddingTop: '2px', paddingBottom: '2px', minWidth: '32px', textAlign: 'center', whiteSpace: 'nowrap', backgroundColor: 'rgba(255,255,255,0.8)', transform: 'translateX(calc(-50% + 16px))', opacity}}>{crew.displayid}</div> : null }
    <div className={"crewMarker" + name} style={{cursor: 'pointer'}} onClick={onClick}>
        <Icon literal={crew.icon ?? type?.icon}></Icon>
    </div>
    </div>;
}

const OLEntityMarker = ({ entity, offset, latitudeField, longitudeField, idField, canUpdate, doUpdate }) => {
    const network = useNetwork();
    const credentials = useCredentials();
    const updateable = canUpdate(credentials, entity);
    const [coords, setCoords] = useState({ latitude: entity[latitudeField], longitude: entity[longitudeField] });

    useEffect(() => setCoords({ latitude: entity[latitudeField], longitude: entity[longitudeField] }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latitudeField, longitudeField, entity[latitudeField], entity[longitudeField], setCoords])

    return <RFeature key={entity[idField]} 
        properties={{ altitudeMode: 'clampToGround' }}
        geometry={new Point(fromLonLat([coords.longitude, coords.latitude]))}
        onPointerDrag={useCallback((event) => {
            if (updateable) {
                if (platformModifierKeyOnly(event)) {
                    const coords = event.map.getCoordinateFromPixel(event.pixel);
                    const [longitude, latitude] = toLonLat(coords);
                    setCoords({ latitude, longitude });
                } else {
                    setCoords({ latitude: entity[latitudeField], longitude: entity[longitudeField] })
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [updateable, latitudeField, longitudeField, entity[latitudeField], entity[longitudeField], setCoords])}
        onPointerDragEnd={useCallback((event) => {
            if (updateable) {
                if (platformModifierKey(event)) {
                    const coords = event.map.getCoordinateFromPixel(event.pixel);
                    const [longitude, latitude] = toLonLat(coords);
                    doUpdate(network, { [idField]: entity[idField], [latitudeField]: latitude, [longitudeField]: longitude });
                } else {
                    setCoords({ latitude: entity[latitudeField], longitude: entity[longitudeField] })
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [latitudeField, longitudeField, entity[latitudeField], entity[longitudeField], entity[idField], network, updateable, doUpdate, setCoords])}
        onPointerMove={useCallback((event) => {
            if (updateable && platformModifierKey(event)) {
                event.map.getTargetElement().style.cursor = "move";
            }
        }, [updateable])}
        onPointerLeave={useCallback((event) => event.map.getTargetElement().style.cursor = 'initial', [])}>
        <ROverlay offset={offset}>
            {entity.icon}
        </ROverlay>
    </RFeature>
}

OLEntityMarker.propTypes = {
    entity: PropTypes.object.isRequired,
    offset: PropTypes.arrayOf(PropTypes.number),
    latitudeField: PropTypes.string.isRequired,
    longitudeField: PropTypes.string.isRequired,
    idField: PropTypes.string.isRequired,
    canUpdate: PropTypes.func.isRequired,
    doUpdate: PropTypes.func.isRequired
}

export const CrewLayer = ({ mode, showLabel = true }) => {
    const { t } = useTranslation();
    const crews = useCrews();
    const types = useTypes();
    const { setSelectedCallsign } = useSelectedCallsign();
    const crewStatuses = useCrewStatuses();
    const id = 'crew_markers';

    const { registerLayer, unregisterLayer, isVisible, getOpacity } = useLayerManager();

    useEffect(() => {
        registerLayer(id, t('crew', { count: 100 }), null, t('crew', { count: 100 }), true, 1)
        return () => unregisterLayer(id);
    }, [registerLayer, unregisterLayer, t]);

    const visible = isVisible(id);
    const opacity = getOpacity(id);

    const crewIcons = useMemo(() => crews.filter(c => c.latitude && c.longitude && crewStatuses.find(s => s.name === c.status)?.group !== 'Unavailable').map(c => {
            const html = crewIconHTML(c, types.find(t => t.type === c.type), showLabel, opacity, () => setSelectedCallsign(c.callsign));
            return { callsign: c.callsign, latitude: c.latitude, longitude: c.longitude, icon: html, leafletIcon: window.L.divIcon({ html: renderToString(html), iconSize: [32, 56] }) }
        }), [crews, types, showLabel, opacity, setSelectedCallsign, crewStatuses]);

    switch (mode) {
        case 'leaflet':
            return crewIcons.map(c => <LeafletMarker key={c.callsign} position={[c.latitude, c.longitude]} icon={c.leafletIcon}></LeafletMarker>)
        case 'ol':
            return <RLayerVector properties={{ title: t('crew', { count: 100 }) }} visible={visible} opacity={opacity}>
                {visible ? crewIcons.map(c => <OLEntityMarker key={c.callsign} entity={c} offset={[-16, -36 - (showLabel ? 20 : 0)]} latitudeField="latitude" longitudeField="longitude" idField="callsign" canUpdate={canUpdateCrew} doUpdate={updateCrew}></OLEntityMarker>) : null}
            </RLayerVector>
        default:
            throw new Error('Unknown mode ' + mode);
    }
}

CrewLayer.propTypes = {
    mode: PropTypes.oneOf(['leaflet','ol']).isRequired,
    showLabel: PropTypes.bool
}

export const JobEntityMarker = ({ entity: job, ...props }) => {
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const assistance = useJobAssistance(job.jobid);
    const updateable = useMemo(() => canUpdateJob(credentials, clientConfig, job, assistance), [credentials, clientConfig, job, assistance]);
    const canUpdate = useCallback((credentials, j) => j.jobid === job.jobid && updateable, [updateable, job.jobid])
    return <OLEntityMarker entity={job} canUpdate={canUpdate} doUpdate={updateJob} idField="jobid" latitudeField="latitude" longitudeField="longitude" {...props}></OLEntityMarker>
}

export const JobLayer = ({ jobs, mode, showLabel = true }) => {
    const { t } = useTranslation();
    const types = useTypes();
    const { setSelectedJobId } = useSelectedJobid();
    const id = 'job_markers';

    const { registerLayer, unregisterLayer, isVisible, getOpacity } = useLayerManager();

    useEffect(() => {
        registerLayer(id, t('job', { count: 100 }), null, t('job', { count: 100 }), true, 1)
        return () => unregisterLayer(id);
    }, [registerLayer, unregisterLayer, t]);

    const visible = isVisible(id);
    const opacity = getOpacity(id);

    const jobIcons = useMemo(() => jobs.filter(j => j.latitude && j.longitude).map(j => {
            const html = jobIconHTML(t, j, types.find(t => t.type === j.type), showLabel, opacity, () => setSelectedJobId(j.jobid));
            return { jobid: j.jobid, latitude: j.latitude, longitude: j.longitude, icon: html, leafletIcon: window.L.divIcon({ html: renderToString(html), iconSize: [32, 56] }) }
        }), [t, jobs, types, showLabel, opacity, setSelectedJobId]);

    switch (mode) {
        case 'leaflet':
            return jobIcons.map(j => <LeafletMarker key={j.jobid} position={[j.latitude, j.longitude]} icon={j.leafletIcon}></LeafletMarker>)
        case 'ol':
            return <RLayerVector properties={{ title: t('job', { count: 100 })}} visible={visible} opacity={opacity}>
                {visible ? jobIcons.map(j => <JobEntityMarker key={j.jobid} entity={j} offset={[-16, -36 - (showLabel ? 20 : 0)]}></JobEntityMarker>) : null}
            </RLayerVector>
        default:
            throw new Error('Unknown mode ' + mode);
    }
}
JobLayer.propTypes = {
    mode: PropTypes.oneOf(['leaflet','ol']).isRequired,
    showLabel: PropTypes.bool,
    jobs: PropTypes.array
}

export const ActiveJobLayer = ({ mode, showLabel = true }) => {
    const jobs = useActiveJobs()
    return <JobLayer mode={mode} showLabel={showLabel} jobs={jobs}></JobLayer>
}

ActiveJobLayer.propTypes = {
    mode: PropTypes.oneOf(['leaflet','ol']).isRequired,
    showLabel: PropTypes.bool
}

export const LocationMarker = ({ location, showLabel, opacity, canUpdate, doUpdate }) => {
    const clientConfig = useClientConfig();
    const { setSelectedLocationID } = useSelectedLocation();
    const entity = useMemo(() => ({ internalid: location.internalid, lat: location.lat, long: location.long, icon: locationIconHTML(location, showLabel, clientConfig.mapTiles?.locationFormat, opacity, setSelectedLocationID ? () => {
        setSelectedLocationID(location.internalid)
    } : null) }), [location, showLabel, opacity, setSelectedLocationID, clientConfig.mapTiles?.locationFormat]);
    return <OLEntityMarker entity={entity} offset={[-16, -16 - (showLabel ? 20 : 0)]} latitudeField="lat" longitudeField="long" idField="internalid" canUpdate={canUpdate} doUpdate={doUpdate}></OLEntityMarker>
}

const LocationGroup = ({ id, group, subgroup, mode, showLabel = true }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const locations = useLocations()
    const { registerLayer, unregisterLayer, isVisible, getOpacity } = useLayerManager();

    useEffect(() => {
        registerLayer(id, t('location', { count: 100 }) + (subgroup ? ' - ' + subgroup : ''), group, t('location', { count: 100 }), clientConfig.trackerOnly && (!clientConfig.enableCrews || clientConfig.hidecrews), 1);
        return () => unregisterLayer(id);
    }, [registerLayer, unregisterLayer, t, group, subgroup, id, clientConfig.enableCrews, clientConfig.hidecrews, clientConfig.trackerOnly]);
    
    const visible = isVisible(id);
    const opacity = getOpacity(id);

    switch (mode) {
        case 'leaflet':
            return locations.filter(l => l.group === group && l.subgroup === subgroup).map(l => <LeafletMarker key={l.internalid} position={[l.lat, l.long]} icon={window.L.divIcon({ html: renderToString(locationIconHTML(l, showLabel, clientConfig.mapTiles?.locationFormat, opacity)), iconSize: [45, 45]})}></LeafletMarker>)
        case 'ol':
                return <>
                {<RLayerVector key={group + '_' + (subgroup ?? '')} properties={{ title: t('location', { count: 100 }) + (subgroup ? ' - ' + subgroup : ''), group: group }} visible={visible}>
                    { visible ? locations.filter(l => l.group === group && l.subgroup === subgroup).map(l => <LocationMarker key={l.internalid} location={l} showLabel={showLabel} opacity={opacity} canUpdate={canUpdateLocation} doUpdate={updateLocation}></LocationMarker>) : null }
                </RLayerVector>}
            </>
        default:
            throw new Error('Unknown mode ' + mode);
    }
}
LocationGroup.propTypes = {
    id: PropTypes.string.isRequired,
    group: PropTypes.string,
    mode: PropTypes.oneOf(['leaflet','ol']).isRequired,
    showLabel: PropTypes.bool
}
export const LocationLayer = ({ mode, showLabel = true }) => {
    const locations = useLocations()
    const groups = useMemo(() => distinct(locations.map(l => l.group)), [locations]);
    const subgroups = useMemo(() => groups.flatMap(g => distinct(locations.filter(l => l.group === g).map(l => l.subgroup)).map(sg => ({ group: g, subgroup: sg }))), [locations, groups]);

    return subgroups.map(s => {
        let id = 'layer_markers';
        if (s.group) {
            id += '_' + s.group.replaceAll(/\s/g, '_').replaceAll(/[^a-zA-Z0-9_]+/g, '');
        }
        if (s.subgroup) {
            id += '_' + s.subgroup.replaceAll(/\s/g, '_').replaceAll(/[^a-zA-Z0-9_]+/g, '');
        }
        return <LocationGroup key={id} id={id} group={s.group} subgroup={s.subgroup} mode={mode} showLabel={showLabel}></LocationGroup>
    });
}
LocationLayer.propTypes = {
    mode: PropTypes.oneOf(['leaflet','ol']).isRequired,
    showLabel: PropTypes.bool
}